import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Trash2, Edit, Plus, Calendar, Download, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const TermsTab = ({
  terms,
  termForm,
  setTermForm,
  termModalOpen,
  setTermModalOpen,
  editingItem,
  setEditingItem,
  handleTermSubmit,
  handleDeleteTerm,
  editTerm
}) => {
  const openNewTerm = () => {
    setEditingItem(null);
    setTermForm({ name: '', start_date: '', end_date: '', is_active: true });
    setTermModalOpen(true);
  };

  const generateICS = (term) => {
    const startDate = new Date(term.start_date);
    const endDate = new Date(term.end_date);
    const formatDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    return `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//UPA School//Term Dates//EN
BEGIN:VEVENT
DTSTART:${formatDate(startDate)}
DTEND:${formatDate(endDate)}
SUMMARY:${term.name}
DESCRIPTION:UPA School Term Period
END:VEVENT
END:VCALENDAR`;
  };

  const downloadICS = (term) => {
    const blob = new Blob([generateICS(term)], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${term.name.replace(/\s+/g, '_')}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Calendar file downloaded - import to Google/Apple Calendar');
  };

  const addToGoogleCalendar = (term) => {
    const startDate = new Date(term.start_date);
    const endDate = new Date(term.end_date);
    const url = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(term.name)}&dates=${startDate.toISOString().replace(/[-:]/g, '').split('.')[0]}/${endDate.toISOString().replace(/[-:]/g, '').split('.')[0]}&details=${encodeURIComponent('UPA School Term Period')}`;
    window.open(url, '_blank');
  };

  return (
    <div data-testid="terms-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-xl font-semibold text-white">Terms</h2>
        <Button 
          onClick={openNewTerm}
          className="bg-yellow-500 hover:bg-yellow-600 text-black"
          data-testid="add-term-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Term
        </Button>
      </div>

      {/* Calendar View */}
      {terms.length > 0 && (
        <Card className="mb-6 bg-[#111] border-[#333]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-white">Term Calendar</h3>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 text-white/70">
                  <span className="w-3 h-3 rounded bg-[#FF007F]"></span> Term Period
                </span>
                <span className="flex items-center gap-1 text-white/70">
                  <span className="w-3 h-3 rounded bg-green-500"></span> Active
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {terms.map(term => {
                const startDate = new Date(term.start_date);
                const endDate = new Date(term.end_date);
                const daysInTerm = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                
                return (
                  <div 
                    key={term.id} 
                    className={`p-4 rounded-lg border-2 ${
                      term.is_active 
                        ? 'border-green-500 bg-green-500/5' 
                        : 'border-[#FF007F]/30 bg-[#FF007F]/5'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-semibold text-white">{term.name}</h4>
                        <p className="text-xs text-white/50">{daysInTerm} days</p>
                      </div>
                      {term.is_active && (
                        <Badge className="bg-green-500 text-white">Active</Badge>
                      )}
                    </div>
                    <div className="space-y-1 mb-3">
                      <p className="text-sm text-white/70">
                        <strong>Start:</strong> {startDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-sm text-white/70">
                        <strong>End:</strong> {endDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => downloadICS(term)}
                        className="flex-1 border-[#333] text-white/70 hover:text-white"
                      >
                        <Download className="w-3 h-3 mr-1" /> .ics
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addToGoogleCalendar(term)}
                        className="flex-1 border-[#333] text-white/70 hover:text-white"
                      >
                        <ExternalLink className="w-3 h-3 mr-1" /> Google
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Terms List */}
      <div className="grid gap-4">
        {terms.map(term => (
          <Card key={term.id} className="bg-[#111] border-[#333]">
            <CardContent className="flex items-center justify-between p-4">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  term.is_active ? 'bg-green-500/20' : 'bg-[#FF007F]/20'
                }`}>
                  <Calendar className={`w-5 h-5 ${term.is_active ? 'text-green-400' : 'text-[#FF007F]'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-white">{term.name}</h3>
                    {term.is_active && (
                      <Badge className="bg-green-500/20 text-green-300 border-green-500/30">Active</Badge>
                    )}
                  </div>
                  <p className="text-sm text-white/50">
                    {new Date(term.start_date).toLocaleDateString('en-GB')} - {new Date(term.end_date).toLocaleDateString('en-GB')}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => editTerm(term)}
                  className="text-white/50 hover:text-white"
                >
                  <Edit className="w-4 h-4" />
                </Button>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => handleDeleteTerm(term.id)}
                  className="text-red-400/50 hover:text-red-400"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {terms.length === 0 && (
          <Card className="bg-[#111] border-[#333]">
            <CardContent className="py-12 text-center text-white/50">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-white/20" />
              <p>No terms yet. Create your first term!</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Term Modal */}
      <Dialog open={termModalOpen} onOpenChange={setTermModalOpen}>
        <DialogContent className="bg-[#111] border-[#333]">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingItem ? 'Edit Term' : 'Add New Term'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-white/70">Name *</Label>
              <Input 
                value={termForm.name} 
                onChange={e => setTermForm({...termForm, name: e.target.value})} 
                placeholder="Spring 2025" 
                className="bg-[#0a0a0a] border-[#333] text-white"
                data-testid="term-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white/70">Start Date *</Label>
                <Input 
                  type="date" 
                  value={termForm.start_date} 
                  onChange={e => setTermForm({...termForm, start_date: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white/70">End Date *</Label>
                <Input 
                  type="date" 
                  value={termForm.end_date} 
                  onChange={e => setTermForm({...termForm, end_date: e.target.value})}
                  className="bg-[#0a0a0a] border-[#333] text-white"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                checked={termForm.is_active}
                onChange={e => setTermForm({...termForm, is_active: e.target.checked})}
                className="w-4 h-4 rounded"
              />
              <Label className="text-white/70">Active Term</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermModalOpen(false)} className="border-[#333] text-white/70">
              Cancel
            </Button>
            <Button onClick={handleTermSubmit} className="bg-yellow-500 hover:bg-yellow-600 text-black" data-testid="save-term-btn">
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TermsTab;
